import { showAlertCallout } from "@/shared/alerts";
import { usePermissionsStore } from "@/shared/stores/permissions.store";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import { showHttpErrorAlertCallout } from "@/shared/utils/show-http-error-alert-callout";
import type {
  UpdateRoleRequestDto,
  UpdateRoleResponseDto,
} from "@/spz-api/model";
import { useGetPermissions } from "@/spz-api/permissions/permissions";
import {
  getGetRoleByIdQueryKey,
  getGetRoleListPaginatedQueryKey,
  updateRole,
  useCreateRole,
} from "@/spz-api/roles/roles";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TreeNode } from "@spz/ui/components/checkbox-tree";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ROLE_FORM_DEFAULT_VALUES } from "../forms/constants/role-form.constants";
import type { RoleFormSchemaValues } from "../forms/schemas/role-form.schema";
import { roleFormSchema } from "../forms/schemas/role-form.schema";

interface UseRoleFormProps {
  initialValues?: Partial<RoleFormSchemaValues>;
  roleId?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  initialUpdatedAt?: string;
}

const useRolePermissions = (selectedPermissionIds: string[] = []) => {
  const { t } = useTranslation();
  const getPermissionsQuery = useGetPermissions();
  const permissionsList = useMemo(() => {
    return getPermissionsQuery.data?.data ?? [];
  }, [getPermissionsQuery.data]);

  const { nodes, setPermissionsList, togglePermission, getSelectedIds } =
    usePermissionsStore();

  // biome-ignore lint/correctness/useExhaustiveDependencies: infinite loop
  useEffect(() => {
    if (permissionsList.length > 0) {
      setPermissionsList(permissionsList, selectedPermissionIds);
    }
  }, [permissionsList, JSON.stringify(selectedPermissionIds)]);

  const options: TreeNode[] = useMemo(
    () =>
      Object.values(nodes).map((node) => ({
        id: node.id,
        label: t(node.label),
        defaultChecked: node.checked,
        disabled: node.disabled,
        tooltip: node.tooltip
          ? t("role_permissions_inherited_from_another")
          : undefined,
      })),
    [nodes, t],
  );

  return {
    options,
    loading: getPermissionsQuery.isPending,
    updateOptions: (selectedIds: string[]) => {
      togglePermission(selectedIds);
    },
    getSelectedIds,
  };
};

export const useRoleForm = ({
  roleId,
  onSuccess,
  onError,
}: UseRoleFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const roleSchema = roleFormSchema(t);

  const form = useForm<RoleFormSchemaValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: ROLE_FORM_DEFAULT_VALUES,
  });
  const permissionIds = form.getValues("permissionIds");
  const permissions = useRolePermissions(permissionIds);
  const createRoleMutation = useCreateRole({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("success_role_created"),
        });

        queryClient.invalidateQueries({
          queryKey: getGetRoleListPaginatedQueryKey({}),
        });
        onSuccess?.();
      },
      onError: (error: unknown) => {
        const { translationKey, displayTypes } =
          getTranslationKeyAndDisplayTypes(error);
        const message = t(translationKey);
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
          form.setError("name", {
            type: "manual",
            message,
          });
        }
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
          showHttpErrorAlertCallout({
            error: message,
          });
        }
        onError?.(error as Error);
      },
    },
  });
  type UpdateRoleMutationVariables = {
    id: string;
    data: UpdateRoleRequestDto;
    headers?: Record<string, string>;
  };

  const updateRoleMutation = useMutation<
    UpdateRoleResponseDto,
    unknown,
    UpdateRoleMutationVariables
  >({
    mutationFn: async (variables) => {
      const { id, data, headers } = variables;
      return updateRole(id, data, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: async () => {
      showAlertCallout({
        type: "success",
        title: t("success_role_updated"),
      });

      queryClient.invalidateQueries({
        queryKey: getGetRoleListPaginatedQueryKey({}),
      });
      queryClient
        .invalidateQueries({
          queryKey: getGetRoleByIdQueryKey(roleId || ""),
        })
        .then(() => {
          onSuccess?.();
        });
    },
    onError: (error: unknown) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
        form.setError("name", {
          type: "manual",
          message,
        });
      }
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showHttpErrorAlertCallout({
          error: message,
        });
        queryClient.invalidateQueries({
          queryKey: getGetRoleListPaginatedQueryKey({}),
        });
      }
    },
  });
  const onSubmit = async (data: RoleFormSchemaValues) => {
    const requestDto = {
      name: data.name.trim(),
      description: data.description?.trim(),
      permissionIds: data.permissionIds || [],
    };

    if (roleId) {
      const updatedAt = form.getValues().updatedAt;
      updateRoleMutation.mutate({
        id: roleId,
        data: { ...requestDto },
        headers: updatedAt ? { "if-match": updatedAt } : undefined,
      });
    } else {
      createRoleMutation.mutate({ data: requestDto });
    }
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isPending: form.formState.isSubmitting,
    isDirty: form.formState.isDirty,
    permissions,
  };
};
